import { useRSOUserList } from "@/api/services/admin/rso";
import { useEnum } from "@/api/services/global/enum";
import { Button } from "@/components/elements/Button";
import { Table } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { findLabelByValue } from "@/utils/object";
import { getDynamicRoute } from "@/utils/url";
import { Stack, Typography, ToggleButton } from "@mui/material";
import { StyledToggleButtonGroup } from "@/components/elements/ToggleButtonGroup/StyledToggleButtonGroup";
import { MRT_ColumnDef } from "material-react-table";
import React, { FC, useMemo, useState, useEffect, useRef } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { truncateWithEllipsis } from "@/utils/string";
import { InnerBox } from "@/components/elements/InnerBox";
import { NewPagination } from "@/components/elements/Pagination/NewPagination";
import { TGetRSOUserListDto } from "@/api/services/admin/rso/dtos/get-match-user-list.dto";
import { formatDate } from "@/utils/datetime";
import { ImportPartnerUserModal } from "@/features/rso/components/ImportPartnerUserModal";
import { useVisible } from "@/hooks/useVisible";
import { IconTrash } from "@/assets/icons/IconTrash";
import { useDialog } from "@/hooks/useDialog";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { deleteRSOUserApi } from "@/api/services/admin/rso/user";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { toast } from "@/utils/toast";
import { showError } from "@/utils/error";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { IconPlus } from "@/assets/icons";

interface PartnerUserTableProps {
  name: string;
  isDeleted?: boolean;
}

export const PartnerUserTable: FC<PartnerUserTableProps> = ({ name: _name, isDeleted }) => {
  const { data: userActiveStatuses } = useEnum("EUserActiveStatus");
  const { id } = useParams();
  const location = useLocation();
  const [userTypeFilter, setUserTypeFilter] = useState<boolean>(true); // true = 顧客ユーザー, false = irohana担当者
  const { data: responseRSOUserList, isLoading } = useRSOUserList(id, userTypeFilter);
  const companyUserListData: TGetRSOUserListDto[] = responseRSOUserList?.data ?? [];
  const confirmDelete = useDialog();
  const queryClient = useQueryClient();
  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  const columns = useMemo<MRT_ColumnDef<TGetRSOUserListDto>[]>(
    () => [
      {
        header: "ユーザー名",
        id: "fullName",
        Cell: ({ row }) => (row.original.fullName ? truncateWithEllipsis(row.original.fullName, 30) : EMPTY_STR.TEXT),
        size: 120,
        muiTableHeadCellProps: {
          sx: {
            maxWidth: 120,
          },
        },
        muiTableBodyCellProps: {
          sx: {
            maxWidth: 120,
          },
        },
      },
      {
        header: "メールアドレス",
        id: "email",
        accessorKey: "email",
        Cell: ({ row }) => (row.original.email ? truncateWithEllipsis(row.original.email, 18) : EMPTY_STR.TEXT),
      },
      {
        header: "所属",
        size: 120,
        Cell: ({ row }) => (row.original.isUser ? "顧客ユーザー" : "irohana担当者"),
      },
      {
        header: "ステータス",
        size: 120,
        Cell: ({ row }) => (row.original.activeStatus ? findLabelByValue(userActiveStatuses, row.original.activeStatus) : EMPTY_STR.TEXT),
      },
      {
        header: "登録日",
        size: 120,
        Cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        header: "最終ログイン日",
        size: 120,
        Cell: ({ row }) => formatDate(row.original.lastLoginTime),
      },
      {
        header: "",
        id: "action",
        Cell: ({ row }) => {
          return (
            <Button component={Link} size="md" variant="outline" to={getDynamicRoute(AppRoutes.rsoUserEdit, { id, userId: row.original.id })}>
              編集
            </Button>
          );
        },
        size: 120,
      },
    ],
    [userActiveStatuses],
  );

  const rsoUserModal = useVisible();

  const { table } = useTable({
    columns,
    rows: companyUserListData ?? [],
    enableRowSelection: true,
    pinningColumns: ["action"],
  });
  const ids = React.useMemo(() => table.getSelectedRowModel().rows.map((row) => row.original.id), [table.getSelectedRowModel()]);

  // Track previous page to reset selection only when page actually changes
  const prevPageRef = useRef<string>("1");

  // Reset row selection when page changes
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const currentPage = query.get("page") || "1";

    // Only reset if page actually changed
    if (prevPageRef.current !== currentPage) {
      table.resetRowSelection();
      prevPageRef.current = currentPage;
    }
  }, [location.search, table]);

  const handleDelete = async () => {
    if (ids.length === 0) return;
    if (!id) return;
    setIsSubmit(true);
    try {
      const agreeToDelete = await confirmDelete.show();
      if (!agreeToDelete) return;
      const rs = await deleteRSOUserApi(Number(id), ids);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      table.resetRowSelection();
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_USER_LIST] });
    } catch (error) {
      showError(error);
    }
    setIsSubmit(false);
  };

  const onCancelConfirm = () => {
    setIsSubmit(false);
    confirmDelete.cancel();
  };

  const handleUserTypeChange = (_event: React.MouseEvent<HTMLElement>, newValue: boolean | null) => {
    if (newValue !== null) {
      // Reset selection khi chuyển tab
      table.resetRowSelection();
      setUserTypeFilter(newValue);
    }
  };

  const getTitle = () => {
    if (userTypeFilter === true) {
      return "顧客ユーザーの一覧";
    }
    return "irohana担当者の一覧";
  };

  return (
    <InnerBox>
      <Stack gap={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="sub16Bold" sx={{ minWidth: "200px", whiteSpace: "nowrap" }}>
              {getTitle()} ({companyUserListData?.length ?? 0})
            </Typography>
            <StyledToggleButtonGroup value={userTypeFilter} exclusive onChange={handleUserTypeChange} size="small">
              <ToggleButton value={true}>顧客ユーザー</ToggleButton>
              <ToggleButton value={false}>irohana担当者</ToggleButton>
            </StyledToggleButtonGroup>
          </Stack>
          {!isDeleted && (
            <Stack direction="row" sx={{ gap: "20px" }}>
              <Button component={Link} to={getDynamicRoute(AppRoutes.rsoUserCreate, { id })} size="sm" variant="outline" startIcon={<IconPlus />}>
                ユーザー追加
              </Button>
            </Stack>
          )}
        </Stack>
        <Table table={table} isLoading={isLoading} />
        <Stack direction="row" justifyContent="space-between">
          <NewPagination totalPage={responseRSOUserList?.pagination?.totalPage} />
          {!isDeleted && (
            <Button
              size="sm"
              variant={"danger"}
              disabled={ids.length === 0 || isSubmit}
              startIcon={<IconTrash />}
              sx={{
                width: "88px",
              }}
              onClick={handleDelete}
            >
              削除
            </Button>
          )}
        </Stack>
      </Stack>
      <ImportPartnerUserModal modal={rsoUserModal} />
      <ConfirmDeleteModal
        open={confirmDelete.open}
        onClose={onCancelConfirm}
        onOk={confirmDelete.confirm}
        message="選択したユーザーを削除してもよろしいですか？"
      />
    </InnerBox>
  );
};
